// ProgramacionConsulta.js
// Componente simplificado para programar una cita desde ConsultaPaciente.
// El paciente ya está definido (id_paciente obligatorio), por lo que
// NO incluye búsqueda de pacientes ni creación de cliente/paciente.
// Esa lógica vive en ProgramarConsulta.js (usado en el calendario, etc.)

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

import api from '../api/api';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const decodeJwtToken = (token) => {
  try {
    const payloadBase64 = token.split('.')[1];
    return JSON.parse(atob(payloadBase64));
  } catch (e) {
    console.error('Error al decodificar el token:', e);
    return null;
  }
};

// ─── Componente ───────────────────────────────────────────────────────────────
/**
 * @param {object}   props
 * @param {boolean}  props.visible        — controla el render
 * @param {Function} props.onClose        — llamado al cancelar o al guardar con éxito
 * @param {number}   props.id_paciente    — obligatorio; el paciente ya está definido
 * @param {string}   [props.initialDate]  — fecha inicial opcional (ISO string)
 */
const ProgramacionConsulta = ({ visible, onClose, id_paciente, initialDate }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const [razonConsulta, setRazonConsulta] = useState('');
  const [fechaCita, setFechaCita]         = useState(dayjs());

  // Credenciales del token
  const [idUsuario, setIdUsuario]   = useState(null);
  const [idSucursal, setIdSucursal] = useState(null);

  // ── Leer token una sola vez ──────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const decoded = decodeJwtToken(token);
    if (decoded) {
      setIdUsuario(decoded.id_usuario);
      setIdSucursal(decoded.id_sucursal);
    } else {
      setError('Error de autenticación. Por favor, vuelve a iniciar sesión.');
    }
  }, []);

  // ── Reiniciar formulario cuando se abre ─────────────────────────────────
  useEffect(() => {
    if (visible) {
      setRazonConsulta('');
      setFechaCita(initialDate ? dayjs(initialDate) : dayjs());
      setError(null);
    }
  }, [visible, initialDate]);

  // ── Guardar ─────────────────────────────────────────────────────────────
  const handleGuardar = async (isExpress = false) => {
    if (!razonConsulta.trim()) {
      setError('Por favor, ingresa la razón de la consulta.');
      return;
    }
    if (!isExpress && !fechaCita) {
      setError('Por favor, selecciona la fecha de la cita.');
      return;
    }
    if (!idUsuario || !idSucursal) {
      setError('No se pudo obtener la información de usuario/sucursal. Vuelve a iniciar sesión.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const payload = {
        id_paciente,
        razon_consulta: razonConsulta,
        fecha_cita: isExpress
          ? new Date().toISOString()
          : fechaCita.format('YYYY-MM-DDTHH:mm:ss'),
        id_usuario:  idUsuario,
        id_sucursal: idSucursal,
        es_express:  isExpress,
        es_pendiente: !isExpress,
      };

      await api.post('/consultas/programar', payload);
      onClose(); // avisa al padre para que refresque y cierre el modal
    } catch (err) {
      if (err.response?.data?.detail && Array.isArray(err.response.data.detail)) {
        const msgs = err.response.data.detail.map(e => `${e.loc.join(' → ')}: ${e.msg}`);
        setError(`Error(es) de validación:\n${msgs.join('\n')}`);
      } else {
        setError('Error al programar consulta: ' + (err.response?.data?.detail || err.message));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Nueva Consulta
        </Typography>

        {/* Razón de consulta */}
        <TextField
          label="Razón de Consulta"
          fullWidth
          multiline
          minRows={2}
          value={razonConsulta}
          onChange={(e) => setRazonConsulta(e.target.value)}
        />

        {/* Fecha y hora */}
        <DateTimePicker
          label="Fecha de Cita"
          value={fechaCita}
          onChange={(val) => setFechaCita(val)}
          format="DD/MM/YYYY hh:mm A"
          ampm
          minutesStep={30}
          slotProps={{
            textField: { fullWidth: true },
            timeClock: { minutesStep: 30 },
          }}
        />

        {error && (
          <Alert severity="error" sx={{ whiteSpace: 'pre-wrap' }}>
            {error}
          </Alert>
        )}

        {/* Acciones */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
          <Button onClick={onClose} variant="outlined" disabled={loading}>
            Cancelar
          </Button>

          {/* Consulta rápida: usa la hora actual como fecha */}

            {/*   <Button
                onClick={() => handleGuardar(true)}
                variant="contained"
                disabled={loading || !razonConsulta.trim()}
                sx={{ bgcolor: 'orange', '&:hover': { bgcolor: 'darkorange' } }}
              >
                {loading ? <CircularProgress size={22} /> : 'Consulta Rápida'}
              </Button>   */}

          {/* Programar con la fecha elegida */}
          <Button
            onClick={() => handleGuardar(false)}
            variant="contained"
            disabled={loading || !razonConsulta.trim() || !fechaCita}
          >
            {loading ? <CircularProgress size={22} /> : 'Programar Cita'}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default ProgramacionConsulta;